// §12 Step 7 — Let's get to know you. Six sections, all errors at once on
// Continue, scroll to first section with an error.
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Input from '@/components/ui/Input/Input';
import OnboardingLayout from './components/OnboardingLayout';
import StepGuard from './components/StepGuard';
import { emptyNominee, useOnboarding } from '@/context/OnboardingContext';
import type { KnowYouDetails } from '@/types/onboarding.types';
import {
  PIN_LOOKUP,
  STATES_AND_UTS,
} from '@/api/constants';
import { OnboardingApiError, submitProfile } from '@/api/onboarding';
import './onboarding.css';

const NAME_REGEX = /^[A-Za-z .'-]{2,100}$/;
type Errors = Record<string, string>;

const MARITAL = ['Single', 'Married', 'Divorced', 'Widowed', 'Separated'];
const RESIDENCE = ['Owned', 'Rented', 'Company provided', 'Parental', 'Other'];
const YEARS = ['Less than 1', '1–3', '3–5', '5 or more'];
const EMPLOYMENT = [
  'Salaried (private)', 'Salaried (government/PSU)', 'Self-employed professional',
  'Business owner', 'Student', 'Homemaker', 'Retired', 'Unemployed',
];
const EMPLOYED = EMPLOYMENT.slice(0, 4);
const INDUSTRIES = [
  'IT/ITES', 'Banking/Finance', 'Manufacturing', 'Trading', 'Healthcare', 'Education',
  'Government', 'Agriculture', 'Real estate', 'Jewellery/Bullion', 'Arms/Defence',
  'Money services', 'Crypto/VDA', 'Other',
];
const INCOMES = [
  'Up to ₹2.5 lakh', '₹2.5–5 lakh', '₹5–10 lakh', '₹10–25 lakh',
  '₹25 lakh–1 crore', 'Above ₹1 crore',
];
const FUND_SOURCES = [
  'Salary', 'Business income', 'Professional fees', 'Savings', 'Interest/dividend',
  'Rental income', 'Agriculture', 'Pension', 'Family support', 'Other',
];
const PURPOSES = [
  'Savings', 'Salary credit', 'Business transactions', 'Investments',
  'Receiving family support', 'Pension', 'Other',
];
const DEPOSITS = ['Up to ₹50,000', '₹50,000–2 lakh', '₹2–5 lakh', '₹5–10 lakh', 'Above ₹10 lakh'];
const QUALIFICATIONS = ['Below 10th', '10th', '12th', 'Graduate', 'Postgraduate', 'Professional', 'Other'];
const RELATIONSHIPS = ['Spouse', 'Son', 'Daughter', 'Father', 'Mother', 'Brother', 'Sister', 'Other'];
const PEP_REL = ['Self', 'Family', 'Close associate'];
const ACCESSIBILITY = ['Visual', 'Hearing', 'Speech', 'Locomotor', 'Other', 'Prefer not to say'];

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  options: string[];
  error?: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

const SelectField = ({ id, label, value, options, error, onChange, placeholder = 'Select' }: SelectFieldProps) => (
  <div className="mb-3">
    <div className="nova-ob-label">{label}</div>
    <select
      id={id}
      className={`form-select ${error ? 'is-invalid' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
    {error && <div className="invalid-feedback d-block">{error}</div>}
  </div>
);

interface YesNoProps {
  label: string;
  value: string;
  onChange: (v: 'YES' | 'NO') => void;
  error?: string;
}

const YesNo = ({ label, value, onChange, error }: YesNoProps) => (
  <div className="mb-3">
    <div className="nova-ob-label">{label}</div>
    <div className="nova-ob-seg">
      {(['YES', 'NO'] as const).map((v) => (
        <button
          key={v}
          type="button"
          className={value === v ? 'selected' : ''}
          onClick={() => onChange(v)}
        >
          {v === 'YES' ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
    {error && <div className="invalid-feedback d-block">{error}</div>}
  </div>
);

const KnowYouForm = () => {
  const navigate = useNavigate();
  const { draft, updateKnowYou, markKnowYouSaved } = useOnboarding();
  const k = draft.knowYou;
  const [errors, setErrors] = useState<Errors>({});
  const [showSummary, setShowSummary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const set = (patch: Partial<KnowYouDetails>) => {
    updateKnowYou(patch);
    const key = Object.keys(patch)[0];
    setErrors((e) => ({ ...e, [key]: '' }));
  };

  const setNominee = (index: number, patch: Partial<KnowYouDetails['nominees'][0]>) => {
    const nominees = k.nominees.map((n, i) => (i === index ? { ...n, ...patch } : n));
    updateKnowYou({ nominees });
  };

  const isEmployed = EMPLOYED.includes(k.employmentStatus);
  const needsEmployer = EMPLOYED.includes(k.employmentStatus);
  const nomineeAge = (dob: string): number => {
    const birth = new Date(dob);
    if (Number.isNaN(birth.getTime())) return 99;
    const now = new Date();
    let a = now.getFullYear() - birth.getFullYear();
    if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) a--;
    return a;
  };

  const totalShare = k.nominees.reduce((sum, n) => sum + (parseInt(n.sharePercent, 10) || 0), 0);

  const handlePin = (pin: string) => {
    const patch: Partial<KnowYouDetails> = { pinCode: pin };
    const hit = PIN_LOOKUP[pin];
    if (hit) {
      patch.city = hit.city;
      patch.state = hit.state;
    }
    set(patch);
  };

  const validate = (): { ok: boolean; firstSection: number } => {
    const e: Errors = {};
    let first = 99;
    const bad = (section: number, key: string, msg: string) => {
      e[key] = msg;
      first = Math.min(first, section);
    };

    // 12.1 Family
    if (!NAME_REGEX.test(k.fatherName.trim())) bad(0, 'fatherName', "Father's name is required.");
    if (!NAME_REGEX.test(k.motherName.trim())) bad(0, 'motherName', "Mother's name is required.");
    if (!k.maritalStatus) bad(0, 'maritalStatus', 'Please select your marital status.');
    if (k.maritalStatus === 'Married' && !NAME_REGEX.test(k.spouseName.trim())) {
      bad(0, 'spouseName', "Spouse's name is required.");
    }

    // 12.2 Address
    if (!k.addressLine1.trim()) bad(1, 'addressLine1', 'Address line 1 is required.');
    if (!/^\d{6}$/.test(k.pinCode)) bad(1, 'pinCode', 'Enter a 6-digit PIN code.');
    if (!k.city.trim()) bad(1, 'city', 'City is required.');
    if (!k.state) bad(1, 'state', 'Please select your state.');
    if (!k.residenceType) bad(1, 'residenceType', 'Please select your residence type.');

    // 12.3 Occupation
    if (!k.employmentStatus) bad(2, 'employmentStatus', 'Please select your employment status.');
    if (needsEmployer && !k.employerName.trim()) bad(2, 'employerName', 'Employer or business name is required.');
    if (isEmployed && !k.industry) bad(2, 'industry', 'Please select your industry.');
    if (!k.annualIncome) bad(2, 'annualIncome', 'Please select your gross annual income.');
    if (k.sourcesOfFunds.length === 0) bad(2, 'sourcesOfFunds', 'Select at least one source of funds.');
    if (k.sourcesOfFunds.includes('Other') && !k.otherSource.trim()) {
      bad(2, 'otherSource', 'Please describe the other source.');
    }

    // 12.4 Usage
    if (!k.purpose) bad(3, 'purpose', 'Please select the purpose of the account.');
    if (!k.monthlyDeposits) bad(3, 'monthlyDeposits', 'Please select your expected monthly deposits.');

    // 12.5 Nominee
    if (!k.wantsNominee) bad(4, 'wantsNominee', 'Please tell us if you want to add a nominee.');
    if (k.wantsNominee === 'NO' && !k.noNomineeConfirm) {
      bad(4, 'noNomineeConfirm', 'Please confirm you understand the implications.');
    }
    if (k.wantsNominee === 'YES') {
      if (!k.nominationType) bad(4, 'nominationType', 'Please choose a nomination type.');
      if (k.nominees.length === 0) bad(4, 'nominees', 'Add at least one nominee.');
      k.nominees.forEach((n, i) => {
        if (!NAME_REGEX.test(n.fullName.trim())) bad(4, `nominee_${i}_name`, 'Nominee name is required.');
        if (!n.relationship) bad(4, `nominee_${i}_rel`, 'Please select the relationship.');
        if (!n.dob || Number.isNaN(new Date(n.dob).getTime())) bad(4, `nominee_${i}_dob`, 'Enter a valid date of birth.');
        if (!n.addressSameAsMine && !n.address.trim()) bad(4, `nominee_${i}_addr`, 'Nominee address is required.');
        if (n.dob && nomineeAge(n.dob) < 18 && !NAME_REGEX.test(n.guardianName.trim())) {
          bad(4, `nominee_${i}_guardian`, 'Guardian name is required for a minor.');
        }
      });
      if (k.nominationType === 'SIMULTANEOUS' && totalShare !== 100) {
        bad(4, 'shareTotal', 'Nominee shares must add up to 100%.');
      }
    }

    // 12.6 Declarations
    if (!k.isPep) bad(5, 'isPep', 'Please answer the PEP question.');
    if (k.isPep === 'YES') {
      if (!k.pepRelationship) bad(5, 'pepRelationship', 'Please select your relationship to the PEP.');
      if (!k.pepPosition.trim()) bad(5, 'pepPosition', 'Position held is required.');
    }
    if (!k.taxResidentOther) bad(5, 'taxResidentOther', 'Please answer the tax residency question.');
    if (k.taxResidentOther === 'YES') {
      if (k.taxRows.length === 0) bad(5, 'taxRows', 'Add at least one country of tax residency.');
      k.taxRows.forEach((r, i) => {
        if (!r.country.trim()) bad(5, `tax_${i}_country`, 'Country is required.');
        if (!r.tin.trim() && !r.noTinReason.trim()) {
          bad(5, `tax_${i}_tin`, 'Enter the TIN or a reason it is not available.');
        }
      });
      if (!k.cityOfBirth.trim()) bad(5, 'cityOfBirth', 'City of birth is required.');
    }
    if (!k.usPerson) bad(5, 'usPerson', 'Please answer the US question.');

    setErrors(e);
    return { ok: Object.keys(e).length === 0, firstSection: first === 99 ? 0 : first };
  };

  const handleContinue = async () => {
    const { ok, firstSection } = validate();
    if (!ok) {
      setShowSummary(true);
      sectionRefs.current[firstSection]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setSaving(true);
    setSubmitError('');
    try {
      // Post all six profile sections to the backend in one call (step 7).
      await submitProfile(k);
      markKnowYouSaved();
      navigate('/open-account/assisted-by');
    } catch (err) {
      setSubmitError(err instanceof OnboardingApiError ? err.message : 'Could not save your details. Please try again.');
      setShowSummary(true);
    } finally {
      setSaving(false);
    }
  };

  const toggleFundSource = (source: string) => {
    const has = k.sourcesOfFunds.includes(source);
    set({
      sourcesOfFunds: has
        ? k.sourcesOfFunds.filter((s) => s !== source)
        : [...k.sourcesOfFunds, source],
    });
  };

  return (
    <OnboardingLayout step={7} heading="Let's get to know you" backTo="/open-account/signature-capture">
      <p>We need a few more details to set up your account, as required by RBI's KYC norms.</p>

      {showSummary && Object.values(errors).some(Boolean) && (
        <div className="nova-ob-error-summary" role="alert">
          Please check the highlighted fields above.
        </div>
      )}

      {/* 12.1 Family details */}
      <div className="nova-ob-section" ref={(el) => { sectionRefs.current[0] = el; }}>
        <div className="nova-ob-section-title">Section 1 of 6 · Family details</div>
        <Input id="fatherName" label="Father's full name" placeholder="As on your documents" value={k.fatherName} error={errors.fatherName} onChange={(e) => set({ fatherName: e.target.value })} />
        <Input id="motherName" label="Mother's full name" placeholder="As on your documents" value={k.motherName} error={errors.motherName} onChange={(e) => set({ motherName: e.target.value })} />
        <SelectField id="maritalStatus" label="Marital status" value={k.maritalStatus} options={MARITAL} error={errors.maritalStatus} onChange={(v) => set({ maritalStatus: v })} />
        {k.maritalStatus === 'Married' && (
          <Input id="spouseName" label="Spouse's full name" value={k.spouseName} error={errors.spouseName} onChange={(e) => set({ spouseName: e.target.value })} />
        )}
      </div>

      {/* 12.2 Address */}
      <div className="nova-ob-section" ref={(el) => { sectionRefs.current[1] = el; }}>
        <div className="nova-ob-section-title">Section 2 of 6 · Address</div>
        <div className="nova-ob-label">Your address</div>
        <Input id="addressLine1" label="Address line 1" placeholder="House / flat, building, street" value={k.addressLine1} error={errors.addressLine1} onChange={(e) => set({ addressLine1: e.target.value })} />
        <Input id="addressLine2" label="Address line 2 (optional)" value={k.addressLine2} onChange={(e) => set({ addressLine2: e.target.value })} />
        <Input id="landmark" label="Landmark (optional)" value={k.landmark} onChange={(e) => set({ landmark: e.target.value })} />
        <Input id="pinCode" label="PIN code" inputMode="numeric" maxLength={6} value={k.pinCode} error={errors.pinCode} helperText="City and state fill in automatically for known PINs." onChange={(e) => handlePin(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        <Input id="city" label="City" value={k.city} error={errors.city} onChange={(e) => set({ city: e.target.value })} />
        <SelectField id="state" label="State" value={k.state} options={STATES_AND_UTS} error={errors.state} onChange={(v) => set({ state: v })} />
        <p className="form-text">We'll send your debit card and cheque book here.</p>
        <SelectField id="residenceType" label="Residence type" value={k.residenceType} options={RESIDENCE} error={errors.residenceType} onChange={(v) => set({ residenceType: v })} />
        <SelectField id="yearsAtAddress" label="Years at current address (optional)" value={k.yearsAtAddress} options={YEARS} onChange={(v) => set({ yearsAtAddress: v })} />
      </div>

      {/* 12.3 Occupation and income */}
      <div className="nova-ob-section" ref={(el) => { sectionRefs.current[2] = el; }}>
        <div className="nova-ob-section-title">Section 3 of 6 · Occupation and income</div>
        <SelectField id="employmentStatus" label="Employment status" value={k.employmentStatus} options={EMPLOYMENT} error={errors.employmentStatus} onChange={(v) => set({ employmentStatus: v })} />
        {needsEmployer && (
          <Input id="employerName" label="Employer or business name" value={k.employerName} error={errors.employerName} onChange={(e) => set({ employerName: e.target.value })} />
        )}
        {isEmployed && (
          <>
            <SelectField id="industry" label="Industry" value={k.industry} options={INDUSTRIES} error={errors.industry} onChange={(v) => set({ industry: v })} />
            <Input id="designation" label="Designation (optional)" value={k.designation} onChange={(e) => set({ designation: e.target.value })} />
          </>
        )}
        <SelectField id="annualIncome" label="Gross annual income" value={k.annualIncome} options={INCOMES} error={errors.annualIncome} onChange={(v) => set({ annualIncome: v })} />
        <div className="mb-3">
          <div className="nova-ob-label">Source of funds</div>
          <div className="d-flex flex-wrap gap-2">
            {FUND_SOURCES.map((s) => (
              <button
                key={s}
                type="button"
                className={`btn btn-sm ${k.sourcesOfFunds.includes(s) ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => toggleFundSource(s)}
              >
                {s}
              </button>
            ))}
          </div>
          {errors.sourcesOfFunds && <div className="invalid-feedback d-block">{errors.sourcesOfFunds}</div>}
        </div>
        {k.sourcesOfFunds.includes('Other') && (
          <Input id="otherSource" label="Other source" placeholder="Tell us where the money comes from" value={k.otherSource} error={errors.otherSource} onChange={(e) => set({ otherSource: e.target.value })} />
        )}
      </div>

      {/* 12.4 Account usage */}
      <div className="nova-ob-section" ref={(el) => { sectionRefs.current[3] = el; }}>
        <div className="nova-ob-section-title">Section 4 of 6 · Account usage</div>
        <SelectField id="purpose" label="Purpose of opening the account" value={k.purpose} options={PURPOSES} error={errors.purpose} onChange={(v) => set({ purpose: v })} />
        <SelectField id="monthlyDeposits" label="Expected monthly deposits" value={k.monthlyDeposits} options={DEPOSITS} error={errors.monthlyDeposits} onChange={(v) => set({ monthlyDeposits: v })} />
        <SelectField id="qualification" label="Educational qualification (optional)" value={k.qualification} options={QUALIFICATIONS} onChange={(v) => set({ qualification: v })} />
      </div>

      {/* 12.5 Nominee */}
      <div className="nova-ob-section" ref={(el) => { sectionRefs.current[4] = el; }}>
        <div className="nova-ob-section-title">Section 5 of 6 · Nominee</div>
        <p className="text-muted" style={{ marginTop: '-0.5rem' }}>You can name up to four nominees.</p>
        <div className="mb-3">
          <div className="nova-ob-label">Would you like to add a nominee?</div>
          <div className="nova-ob-seg">
            <button type="button" className={k.wantsNominee === 'YES' ? 'selected' : ''} onClick={() => set({ wantsNominee: 'YES' })}>
              Yes, add nominee
            </button>
            <button type="button" className={k.wantsNominee === 'NO' ? 'selected' : ''} onClick={() => set({ wantsNominee: 'NO' })}>
              No, I don't want to nominate
            </button>
          </div>
          {errors.wantsNominee && <div className="invalid-feedback d-block">{errors.wantsNominee}</div>}
        </div>

        {k.wantsNominee === 'NO' && (
          <div className="form-check mb-3">
            <input id="noNomineeConfirm" type="checkbox" className="form-check-input" checked={k.noNomineeConfirm} onChange={(e) => set({ noNomineeConfirm: e.target.checked })} />
            <label htmlFor="noNomineeConfirm" className="form-check-label" style={{ fontSize: '0.85rem' }}>
              I understand the implications of not nominating and choose not to add a nominee.
            </label>
            {errors.noNomineeConfirm && <div className="invalid-feedback d-block">{errors.noNomineeConfirm}</div>}
          </div>
        )}

        {k.wantsNominee === 'YES' && (
          <>
            <div className="mb-3">
              <div className="nova-ob-label">Nomination type</div>
              <div className="nova-ob-seg">
                <button type="button" className={k.nominationType === 'SIMULTANEOUS' ? 'selected' : ''} onClick={() => set({ nominationType: 'SIMULTANEOUS' })}>
                  Simultaneous (split by %)
                </button>
                <button type="button" className={k.nominationType === 'SUCCESSIVE' ? 'selected' : ''} onClick={() => set({ nominationType: 'SUCCESSIVE' })}>
                  Successive (in order of priority)
                </button>
              </div>
              {errors.nominationType && <div className="invalid-feedback d-block">{errors.nominationType}</div>}
            </div>

            {k.nominees.map((n, i) => (
              <div className="nova-ob-card" key={i}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-bold">Nominee {i + 1}</div>
                  <div className="d-flex gap-1">
                    {k.nominationType === 'SUCCESSIVE' && i > 0 && (
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => {
                        const nominees = [...k.nominees];
                        [nominees[i - 1], nominees[i]] = [nominees[i], nominees[i - 1]];
                        updateKnowYou({ nominees });
                      }}>
                        ↑
                      </button>
                    )}
                    {k.nominationType === 'SUCCESSIVE' && i < k.nominees.length - 1 && (
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => {
                        const nominees = [...k.nominees];
                        [nominees[i], nominees[i + 1]] = [nominees[i + 1], nominees[i]];
                        updateKnowYou({ nominees });
                      }}>
                        ↓
                      </button>
                    )}
                    {k.nominees.length > 1 && (
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => updateKnowYou({ nominees: k.nominees.filter((_, idx) => idx !== i) })}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <Input id={`nominee_${i}_name`} label="Full name" value={n.fullName} error={errors[`nominee_${i}_name`]} onChange={(e) => setNominee(i, { fullName: e.target.value })} />
                <SelectField id={`nominee_${i}_rel`} label="Relationship" value={n.relationship} options={RELATIONSHIPS} error={errors[`nominee_${i}_rel`]} onChange={(v) => setNominee(i, { relationship: v })} />
                <div className="mb-3">
                  <div className="nova-ob-label">Date of birth</div>
                  <input type="date" className={`form-control ${errors[`nominee_${i}_dob`] ? 'is-invalid' : ''}`} value={n.dob} onChange={(e) => setNominee(i, { dob: e.target.value })} />
                  {errors[`nominee_${i}_dob`] && <div className="invalid-feedback d-block">{errors[`nominee_${i}_dob`]}</div>}
                </div>
                {k.nominationType === 'SIMULTANEOUS' && (
                  <Input id={`nominee_${i}_share`} label="Share %" inputMode="numeric" maxLength={3} value={n.sharePercent} onChange={(e) => setNominee(i, { sharePercent: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
                )}
                <div className="form-check mb-2">
                  <input id={`nominee_${i}_same`} type="checkbox" className="form-check-input" checked={n.addressSameAsMine} onChange={(e) => setNominee(i, { addressSameAsMine: e.target.checked })} />
                  <label htmlFor={`nominee_${i}_same`} className="form-check-label">Nominee address same as mine</label>
                </div>
                {!n.addressSameAsMine && (
                  <Input id={`nominee_${i}_addr`} label="Nominee address" placeholder="Full address with PIN code" value={n.address} error={errors[`nominee_${i}_addr`]} onChange={(e) => setNominee(i, { address: e.target.value })} />
                )}
                {n.dob && nomineeAge(n.dob) < 18 && (
                  <>
                    <p className="form-text">This nominee is under 18, so a guardian is needed to receive the money on their behalf.</p>
                    <Input id={`nominee_${i}_guardian`} label="Guardian name" value={n.guardianName} error={errors[`nominee_${i}_guardian`]} onChange={(e) => setNominee(i, { guardianName: e.target.value })} />
                    <SelectField id={`nominee_${i}_grel`} label="Guardian relationship" value={n.guardianRelationship} options={RELATIONSHIPS} onChange={(v) => setNominee(i, { guardianRelationship: v })} />
                  </>
                )}
              </div>
            ))}

            {errors.nominees && <div className="invalid-feedback d-block mb-2">{errors.nominees}</div>}
            {k.nominationType === 'SIMULTANEOUS' && (
              <p className={totalShare === 100 ? 'text-success' : 'text-muted'}>Total share: {totalShare}%</p>
            )}
            {errors.shareTotal && <div className="invalid-feedback d-block mb-2">{errors.shareTotal}</div>}
            {k.nominees.length < 4 && (
              <button type="button" className="btn btn-outline-primary w-100 mb-3" onClick={() => updateKnowYou({ nominees: [...k.nominees, emptyNominee()] })}>
                Add another nominee
              </button>
            )}
          </>
        )}
      </div>

      {/* 12.6 Declarations */}
      <div className="nova-ob-section" ref={(el) => { sectionRefs.current[5] = el; }}>
        <div className="nova-ob-section-title">Section 6 of 6 · Declarations</div>
        <YesNo label="Are you, or any of your immediate family members or close associates, a Politically Exposed Person (PEP)?" value={k.isPep} error={errors.isPep} onChange={(v) => set({ isPep: v })} />
        {k.isPep === 'YES' && (
          <>
            <SelectField id="pepRelationship" label="Relationship to PEP" value={k.pepRelationship} options={PEP_REL} error={errors.pepRelationship} onChange={(v) => set({ pepRelationship: v })} />
            <Input id="pepPosition" label="Position held" value={k.pepPosition} error={errors.pepPosition} onChange={(e) => set({ pepPosition: e.target.value })} />
          </>
        )}

        <YesNo label="Are you a tax resident of any country other than India?" value={k.taxResidentOther} error={errors.taxResidentOther} onChange={(v) => set({ taxResidentOther: v })} />
        {k.taxResidentOther === 'YES' && (
          <>
            {k.taxRows.map((row, i) => (
              <div className="nova-ob-card" key={i}>
                <div className="fw-bold mb-2">Country {i + 1}</div>
                <Input id={`tax_${i}_country`} label="Country" value={row.country} error={errors[`tax_${i}_country`]} onChange={(e) => {
                  const taxRows = k.taxRows.map((r, idx) => (idx === i ? { ...r, country: e.target.value } : r));
                  updateKnowYou({ taxRows });
                }} />
                <Input id={`tax_${i}_tin`} label="Tax Identification Number (TIN)" value={row.tin} error={errors[`tax_${i}_tin`]} onChange={(e) => {
                  const taxRows = k.taxRows.map((r, idx) => (idx === i ? { ...r, tin: e.target.value } : r));
                  updateKnowYou({ taxRows });
                }} />
                {!row.tin && (
                  <Input id={`tax_${i}_reason`} label="Reason if no TIN is available" value={row.noTinReason} onChange={(e) => {
                    const taxRows = k.taxRows.map((r, idx) => (idx === i ? { ...r, noTinReason: e.target.value } : r));
                    updateKnowYou({ taxRows });
                  }} />
                )}
              </div>
            ))}
            {errors.taxRows && <div className="invalid-feedback d-block mb-2">{errors.taxRows}</div>}
            <button type="button" className="btn btn-outline-primary w-100 mb-3" onClick={() => updateKnowYou({ taxRows: [...k.taxRows, { country: '', tin: '', noTinReason: '' }] })}>
              Add another country
            </button>
            <Input id="countryOfBirth" label="Country of birth" value={k.countryOfBirth} onChange={(e) => set({ countryOfBirth: e.target.value })} />
            <Input id="cityOfBirth" label="City of birth" value={k.cityOfBirth} error={errors.cityOfBirth} onChange={(e) => set({ cityOfBirth: e.target.value })} />
            <Input id="citizenship" label="Citizenship" value={k.citizenship} onChange={(e) => set({ citizenship: e.target.value })} />
          </>
        )}

        <YesNo label="Are you a US citizen or US green-card holder?" value={k.usPerson} error={errors.usPerson} onChange={(v) => set({ usPerson: v })} />

        <div className="mb-3">
          <div className="nova-ob-label">Residential status</div>
          <div className="nova-ob-seg">
            <button type="button" className={k.residentialStatus === 'RESIDENT' ? 'selected' : ''} onClick={() => set({ residentialStatus: 'RESIDENT' })}>
              Resident Indian
            </button>
            <button type="button" className={k.residentialStatus === 'NRI' ? 'selected' : ''} onClick={() => set({ residentialStatus: 'NRI' })}>
              NRI
            </button>
          </div>
          {k.residentialStatus === 'NRI' && (
            <div className="nova-ob-info mt-2">Please open an NRE/NRO account instead.</div>
          )}
        </div>

        <SelectField id="accessibility" label="Do you require any accessibility support? (optional)" value={k.accessibility} options={ACCESSIBILITY} onChange={(v) => set({ accessibility: v })} />
      </div>

      {submitError && (
        <div className="alert alert-danger py-2" role="alert">{submitError}</div>
      )}

      <div className="nova-ob-footer">
        <div className="nova-ob-footer-inner">
          <button type="button" className="btn btn-outline-primary btn-lg w-100" onClick={() => navigate('/open-account/signature-capture')}>
            Back
          </button>
          <button type="button" className="btn btn-primary btn-lg w-100 fw-semibold nova-btn" disabled={saving} onClick={handleContinue}>
            {saving && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
            Continue
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
};

const KnowYou = () => (
  <StepGuard requires={(d) => d.signatureCaptured}>
    <KnowYouForm />
  </StepGuard>
);

export default KnowYou;
